// pairing-client.ts - client-side ECDH key envelope for short-code pairing.
//
// Protocol (matches server internal/pairing):
//   1. Device B generates ECDH keypair (P-256).
//   2. Device B submits publicKey with a 6-char code.
//   3. Device A encrypts roomSecret with Device B's publicKey.
//   4. Device B fetches the encrypted envelope and decrypts with privateKey.
//
// The server NEVER sees roomSecret, privateKey, or any plaintext.

// Generate an ECDH key pair using P-256 curve.
export async function generateECDHKeyPair(): Promise<{
  publicKey: string;
  privateKey: CryptoKey;
}> {
  const pair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits'],
  );
  const pubKeyBytes = await crypto.subtle.exportKey('raw', pair.publicKey);
  const publicKey = bytesToBase64(pubKeyBytes);
  return { publicKey, privateKey: pair.privateKey };
}

// Encrypt room credentials (roomSecret + accessToken) using the requester's
// ECDH public key. Device A calls this after verifying the requester's
// fingerprint. Both values are needed by Device B to read+write items.
export async function encryptRoomSecret(
  requesterPublicKeyBase64: string,
  roomSecret: string,
  accessToken?: string,
): Promise<string> {
  const pubKeyBytes = base64ToBytes(requesterPublicKeyBase64);
  const pubKeyBuf = toArrayBuffer(pubKeyBytes);
  const pubKey = await crypto.subtle.importKey(
    'raw',
    pubKeyBuf,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );

  const ephemeralPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits'],
  );

  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: pubKey },
    ephemeralPair.privateKey,
    256,
  );

  const sharedBuf = toArrayBuffer(new Uint8Array(sharedBits));
  const aesKey = await crypto.subtle.importKey('raw', sharedBuf, 'AES-GCM', false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  // Pack roomSecret + accessToken as JSON so Device B gets both.
  const payload = JSON.stringify({ roomSecret, accessToken: accessToken ?? '' });
  const encoded = new TextEncoder().encode(payload);
  const encodedBuf = toArrayBuffer(encoded);
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    aesKey,
    encodedBuf,
  );

  const ephemeralPubBytes = await crypto.subtle.exportKey('raw', ephemeralPair.publicKey);

  const envelope = new Uint8Array(ephemeralPubBytes.byteLength + iv.byteLength + cipherBuf.byteLength);
  envelope.set(new Uint8Array(ephemeralPubBytes), 0);
  envelope.set(iv, ephemeralPubBytes.byteLength);
  envelope.set(new Uint8Array(cipherBuf), ephemeralPubBytes.byteLength + iv.byteLength);

  return bytesToBase64(envelope.buffer);
}

// Decrypt the envelope using Device B's private key to recover room
// credentials (roomSecret + accessToken).
export async function decryptEnvelope(
  privateKey: CryptoKey,
  envelopeBase64: string,
): Promise<{ roomSecret: string; accessToken: string }> {
  const envelopeBytes = base64ToBytes(envelopeBase64);

  const ephemeralPubBytes = envelopeBytes.slice(0, 65);
  const iv = envelopeBytes.slice(65, 77);
  const ciphertext = envelopeBytes.slice(65 + 12);

  const ephemeralPubKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(ephemeralPubBytes),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );

  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: ephemeralPubKey },
    privateKey,
    256,
  );

  const sharedBuf = toArrayBuffer(new Uint8Array(sharedBits));
  const aesKey = await crypto.subtle.importKey('raw', sharedBuf, 'AES-GCM', false, ['decrypt']);
  const ivBuf = toArrayBuffer(iv);
  const cipherBuf = toArrayBuffer(ciphertext);
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: ivBuf as BufferSource },
    aesKey,
    cipherBuf,
  );

  const text = new TextDecoder().decode(plainBuf);
  try {
    const parsed = JSON.parse(text);
    return {
      roomSecret: parsed.roomSecret ?? '',
      accessToken: parsed.accessToken ?? '',
    };
  } catch {
    // Backward compat: old envelopes contained only roomSecret.
    return { roomSecret: text, accessToken: '' };
  }
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  return buf;
}

function bytesToBase64(buf: ArrayBuffer | Uint8Array): string {
  const b = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
